import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;

const jobTags = [
  { value: 'it_developer', label: 'I.T Developer', icon: '💻' },
  { value: 'cybersecurity', label: 'Cybersecurity', icon: '🔒' },
  { value: 'engineer', label: 'Engineer', icon: '⚙️' },
  { value: 'food_and_service', label: 'Food & Service', icon: '🍽️' },
  { value: 'sales', label: 'Sales', icon: '📈' },
  { value: 'marketing', label: 'Marketing', icon: '📣' },
  { value: 'administrative', label: 'Administrative', icon: '📋' },
  { value: 'accounting', label: 'Accounting', icon: '💰' },
  { value: 'public_relations', label: 'Public Relations', icon: '🤝' },
  { value: 'healthcare', label: 'Healthcare', icon: '🏥' },
  { value: 'agriculture', label: 'Agriculture', icon: '🌾' },
  { value: 'executive', label: 'Executive', icon: '👔' },
];

const currencies = {
  USD: '$', EUR: '€', GBP: '£', SEK: 'kr', NOK: 'kr',
  DKK: 'kr', CHF: 'Fr', CAD: '$', AUD: '$', JPY: '¥'
};

const styles = {
  global: `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif; background: linear-gradient(135deg, #e8f0e9 0%, #d4e5d8 50%, #c5dde0 100%); min-height: 100vh; color: #3d4f4f; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
    @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.02); } }
    @keyframes searching { 0% { opacity: 1; } 50% { opacity: 0.6; } 100% { opacity: 1; } }
    @keyframes timerPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
    button:hover:not(:disabled) { transform: translateY(-2px); }
    button:active:not(:disabled) { transform: translateY(0); }
    button:disabled { opacity: 0.6; cursor: not-allowed; }
    input[type="range"] { -webkit-appearance: none; width: 100%; height: 8px; border-radius: 4px; background: rgba(124, 152, 133, 0.2); }
    input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; width: 24px; height: 24px; border-radius: 50%; background: linear-gradient(135deg, #7c9885 0%, #5a7a65 100%); cursor: pointer; }
  `,
  
  // Navigation styles for hiring managers
  nav: {
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(124, 152, 133, 0.15)',
    padding: '0 40px',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  navInner: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '70px',
  },
  navLogo: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#3d4f4f',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  navLinks: {
    display: 'flex',
    gap: '8px',
  },
  navLink: {
    padding: '10px 20px',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '500',
    color: '#5a7070',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    background: 'transparent',
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'inherit',
  },
  navLinkActive: {
    background: 'rgba(124, 152, 133, 0.15)',
    color: '#3d4f4f',
  },
  
  // Main layout
  pageContainer: {
    minHeight: '100vh',
    display: 'flex',
    position: 'relative',
  },
  
  mainContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    paddingRight: '340px', // Space for messaging sidebar
    position: 'relative',
    overflow: 'hidden',
  },
  
  bgCircle1: { position: 'absolute', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(124, 152, 133, 0.15)', top: '-100px', right: '340px', animation: 'float 8s ease-in-out infinite', zIndex: 0 },
  bgCircle2: { position: 'absolute', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(147, 180, 186, 0.12)', bottom: '-80px', left: '-80px', animation: 'float 10s ease-in-out infinite 1s', zIndex: 0 },
  bgCircle3: { position: 'absolute', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(200, 180, 160, 0.1)', top: '40%', left: '10%', animation: 'float 6s ease-in-out infinite 0.5s', zIndex: 0 },
  
  card: { background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '40px 36px', width: '100%', maxWidth: '580px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(61, 79, 79, 0.08)', border: '1px solid rgba(255, 255, 255, 0.6)', animation: 'fadeIn 0.6s ease-out', position: 'relative', zIndex: 1 },
  cardWide: { maxWidth: '1200px', padding: '32px' },
  cardMatchView: { maxWidth: 'none', width: 'calc(100% - 40px)', maxHeight: 'none', overflowY: 'visible' },
  
  logo: { textAlign: 'center', marginBottom: '24px' },
  logoText: { fontSize: '28px', fontWeight: '600', color: '#3d4f4f' },
  subtitle: { fontSize: '16px', color: '#6b8080', marginTop: '8px' },
  
  avatar: { width: '70px', height: '70px', background: 'linear-gradient(135deg, #93b4ba 0%, #7c9885 100%)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '28px', color: 'white', fontWeight: '600', boxShadow: '0 8px 24px rgba(124, 152, 133, 0.3)' },
  welcomeText: { fontSize: '22px', fontWeight: '600', color: '#3d4f4f', marginBottom: '4px', textAlign: 'center' },
  userEmail: { fontSize: '14px', color: '#6b8080', marginBottom: '8px', textAlign: 'center' },
  userRole: { display: 'inline-block', padding: '5px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '500', marginBottom: '20px' },
  roleJobSeeker: { background: 'rgba(147, 180, 186, 0.2)', color: '#4a7a85' },
  roleHiringManager: { background: 'rgba(124, 152, 133, 0.2)', color: '#5a7a65' },
  divider: { height: '1px', background: 'rgba(124, 152, 133, 0.15)', margin: '20px 0' },
  
  interviewButtonContainer: { marginBottom: '16px' },
  getInterviewButton: { width: '100%', padding: '18px 28px', border: 'none', borderRadius: '14px', fontSize: '18px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.3s ease', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  getInterviewButtonActive: { background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)', color: 'white', boxShadow: '0 8px 24px rgba(124, 152, 133, 0.4)' },
  getInterviewButtonSearching: { background: 'linear-gradient(135deg, #b47878 0%, #8a5a5a 100%)', color: 'white', boxShadow: '0 8px 24px rgba(180, 120, 120, 0.4)', animation: 'pulse 2s ease-in-out infinite' },
  searchingText: { animation: 'searching 1.5s ease-in-out infinite' },
  
  sliderContainer: { marginTop: '16px', padding: '14px', background: 'rgba(124, 152, 133, 0.05)', borderRadius: '12px' },
  sliderLabel: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  sliderTitle: { fontSize: '14px', fontWeight: '500', color: '#5a7070' },
  sliderValue: { fontSize: '15px', fontWeight: '600', color: '#7c9885' },
  slider: { width: '100%', cursor: 'pointer' },
  sliderMarks: { display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '11px', color: '#9ab0a5' },
  
  tagSectionTitle: { fontSize: '14px', fontWeight: '500', color: '#5a7070', marginBottom: '10px' },
  tagGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  tagButton: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', border: '2px solid rgba(124, 152, 133, 0.2)', borderRadius: '20px', background: 'rgba(255, 255, 255, 0.8)', cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: 'inherit', fontSize: '13px', fontWeight: '500', color: '#5a7070' },
  tagButtonSelected: { borderColor: '#7c9885', background: 'rgba(124, 152, 133, 0.15)', color: '#3d4f4f', boxShadow: '0 2px 8px rgba(124, 152, 133, 0.2)' },
  tagIcon: { fontSize: '16px' },
  
  statusMessage: { textAlign: 'center', fontSize: '14px', color: '#6b8080', marginTop: '10px' },
  matchCount: { textAlign: 'center', fontSize: '14px', color: '#7c9885', fontWeight: '600', marginTop: '6px' },
  locationStatus: { textAlign: 'center', fontSize: '12px', color: '#9ab0a5', marginTop: '6px' },
  
  buttonGroup: { display: 'flex', flexDirection: 'column', gap: '10px' },
  button: { padding: '12px 20px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', fontFamily: 'inherit' },
  buttonPrimary: { background: 'linear-gradient(135deg, #7c9885 0%, #6a8a74 100%)', color: 'white', boxShadow: '0 4px 12px rgba(124, 152, 133, 0.35)' },
  buttonSecondary: { background: 'rgba(124, 152, 133, 0.1)', color: '#5a7070' },
  
  message: { padding: '10px 14px', borderRadius: '10px', fontSize: '14px', textAlign: 'center', marginBottom: '14px' },
  messageError: { background: 'rgba(180, 120, 120, 0.12)', color: '#8a5a5a', border: '1px solid rgba(180, 120, 120, 0.2)' },
  messageSuccess: { background: 'rgba(124, 152, 133, 0.12)', color: '#5a7a65', border: '1px solid rgba(124, 152, 133, 0.2)' },

  // Match view styles
  matchCard: { background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '20px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
  matchHeader: { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid rgba(124, 152, 133, 0.15)' },
  matchAvatar: { width: '56px', height: '56px', background: 'linear-gradient(135deg, #93b4ba 0%, #7c9885 100%)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: 'white', fontWeight: '600' },
  matchName: { fontSize: '20px', fontWeight: '600', color: '#3d4f4f' },
  matchRole: { fontSize: '13px', color: '#6b8080', marginTop: '2px' },
  matchSection: { marginBottom: '16px' },
  matchSectionTitle: { fontSize: '11px', fontWeight: '700', color: '#7c9885', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  matchText: { fontSize: '14px', color: '#5a7070', lineHeight: '1.6' },
  matchItem: { marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid rgba(124, 152, 133, 0.1)' },
  matchItemTitle: { fontSize: '15px', fontWeight: '600', color: '#3d4f4f' },
  matchItemSub: { fontSize: '13px', color: '#6b8080', marginTop: '2px' },
  
  timer: { textAlign: 'center', marginBottom: '20px', padding: '16px', background: 'rgba(124, 152, 133, 0.08)', borderRadius: '12px' },
  timerText: { fontSize: '52px', fontWeight: '700', color: '#3d4f4f', animation: 'timerPulse 1s infinite' },
  timerWarning: { color: '#b47878' },
  timerLabel: { fontSize: '13px', color: '#6b8080', marginTop: '4px' },
  
  actionButtons: { display: 'flex', gap: '12px' },
  acceptButton: { flex: 1, padding: '16px', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)', color: 'white', fontFamily: 'inherit', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(124, 152, 133, 0.35)' },
  rejectButton: { flex: 1, padding: '16px', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', background: 'linear-gradient(135deg, #b47878 0%, #8a5a5a 100%)', color: 'white', fontFamily: 'inherit', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(180, 120, 120, 0.35)' },
  waitingText: { textAlign: 'center', color: '#7c9885', fontSize: '15px', padding: '16px', background: 'rgba(124, 152, 133, 0.1)', borderRadius: '12px', fontWeight: '500' },
  
  badge: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '500', marginRight: '8px', marginBottom: '8px' },
  badgeCategory: { background: 'rgba(200, 180, 160, 0.2)', color: '#6a5a4a' },
  badgeEmployment: { background: 'rgba(124, 152, 133, 0.15)', color: '#5a7a65' },
  salaryText: { fontSize: '18px', fontWeight: '600', color: '#3d4f4f' },

  // Messaging sidebar styles
  messagingSidebar: {
    position: 'fixed',
    right: 0,
    top: 0,
    bottom: 0,
    width: '320px',
    background: 'white',
    boxShadow: '-4px 0 20px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 100,
  },
  
  sidebarHeader: {
    padding: '20px',
    borderBottom: '1px solid rgba(124, 152, 133, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  
  sidebarTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3d4f4f',
  },
  
  conversationsList: {
    flex: 1,
    overflowY: 'auto',
  },
  
  conversationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 20px',
    cursor: 'pointer',
    transition: 'background 0.2s',
    borderBottom: '1px solid rgba(124, 152, 133, 0.08)',
  },
  
  conversationItemActive: {
    background: 'rgba(124, 152, 133, 0.1)',
  },
  
  conversationItemHover: {
    background: 'rgba(124, 152, 133, 0.05)',
  },
  
  conversationAvatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #93b4ba 0%, #7c9885 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    color: 'white',
    fontWeight: '600',
    flexShrink: 0,
  },
  
  conversationInfo: {
    flex: 1,
    minWidth: 0,
  },
  
  conversationName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '2px',
  },
  
  conversationPreview: {
    fontSize: '12px',
    color: '#6b8080',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  
  conversationMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },
  
  conversationTime: {
    fontSize: '11px',
    color: '#9ab0a5',
  },
  
  unreadBadge: {
    background: '#7c9885',
    color: 'white',
    fontSize: '11px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '10px',
    minWidth: '18px',
    textAlign: 'center',
  },
  
  chatPanel: {
    position: 'fixed',
    right: '320px',
    bottom: 0,
    width: '340px',
    height: '450px',
    background: 'white',
    borderRadius: '12px 12px 0 0',
    boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 99,
  },
  
  chatHeader: {
    padding: '14px 16px',
    borderBottom: '1px solid rgba(124, 152, 133, 0.15)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  
  chatHeaderAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #93b4ba 0%, #7c9885 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    color: 'white',
    fontWeight: '600',
  },
  
  chatHeaderInfo: {
    flex: 1,
  },
  
  chatHeaderName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3d4f4f',
  },
  
  chatHeaderRole: {
    fontSize: '11px',
    color: '#6b8080',
  },
  
  chatCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#6b8080',
    padding: '4px',
  },
  
  chatMessages: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  
  chatMessage: {
    maxWidth: '80%',
    padding: '10px 14px',
    borderRadius: '16px',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  
  chatMessageOwn: {
    alignSelf: 'flex-end',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    color: 'white',
    borderBottomRightRadius: '4px',
  },
  
  chatMessageOther: {
    alignSelf: 'flex-start',
    background: 'rgba(124, 152, 133, 0.1)',
    color: '#3d4f4f',
    borderBottomLeftRadius: '4px',
  },
  
  chatInputContainer: {
    padding: '12px 16px',
    borderTop: '1px solid rgba(124, 152, 133, 0.15)',
    display: 'flex',
    gap: '10px',
  },
  
  chatInput: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '20px',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  
  chatSendBtn: {
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '38px',
    height: '38px',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  emptyConversations: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#6b8080',
  },
  
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  
  emptyText: {
    fontSize: '14px',
  },
};

function HomePage({ user, setUser }) {
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const timerRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const matchedUserRef = useRef(null);
  const activeChatRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [searchRadius, setSearchRadius] = useState(user.searchRadius || 100);
  const [selectedTags, setSelectedTags] = useState(user.selectedTags || []);
  const [matchCount, setMatchCount] = useState(null);
  const [locationStatus, setLocationStatus] = useState('');
  
  // Match state
  const [matchedUser, setMatchedUser] = useState(null);
  const [matchId, setMatchId] = useState(null);
  const [timeLeft, setTimeLeft] = useState(90);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [matchedFileUrl, setMatchedFileUrl] = useState(null);

  // Messaging state
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');

  const isSearching = user.lookingForInterview;
  const isJobSeeker = user.role === 'job_seeker';

  // Fetch signed URL for matched user's file (CV or job listing)
  const fetchMatchedFileUrl = async (matchedUserId, fileType) => {
    try {
      const endpoint = fileType === 'cv' 
        ? `${API_URL}/auth/cv/${matchedUserId}`
        : `${API_URL}/auth/job-listing/${matchedUserId}`;
      
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setMatchedFileUrl(data.data.url);
      }
    } catch (error) {
      console.error('Error fetching file URL:', error);
    }
  };

  // Keep matchedUser ref in sync
  useEffect(() => {
    matchedUserRef.current = matchedUser;
  }, [matchedUser]);

  // Keep activeChat ref in sync
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  // Initialize Socket.IO
  useEffect(() => {
    const token = localStorage.getItem('token');
    socketRef.current = io(SOCKET_URL, { auth: { token } });

    socketRef.current.on('connect', () => {
      console.log('Socket connected');
      if (user.lookingForInterview) {
        socketRef.current.emit('start-search');
      }
    });

    socketRef.current.on('match-found', ({ matchId: mId, matchedUser: mu }) => {
      console.log('Match found!', mu);
      setMatchedUser(mu);
      setMatchId(mId);
      setTimeLeft(90);
      setHasAccepted(false);
      setMatchedFileUrl(null);
      
      // Fetch signed URL for the matched user's file
      if (mu.role === 'hiring_manager' && mu.jobListingFile && mu.jobListingFile.s3Key) {
        fetchMatchedFileUrl(mu._id, 'job-listing');
      } else if (mu.role === 'job_seeker' && mu.cvFile && mu.cvFile.s3Key) {
        fetchMatchedFileUrl(mu._id, 'cv');
      }
    });

    socketRef.current.on('match-accepted', ({ roomId, isInitiator, peerId }) => {
      console.log('Both accepted! Starting interview... isInitiator:', isInitiator);
      sessionStorage.setItem('interviewData', JSON.stringify({
        roomId,
        isInitiator,
        peerId,
        matchedUser: matchedUserRef.current
      }));
      navigate('/interview');
    });

    socketRef.current.on('match-rejected', () => {
      console.log('Match rejected or timed out');
      setMatchedUser(null);
      setMatchId(null);
      setHasAccepted(false);
      setMatchedFileUrl(null);
      setMessage({ text: 'Match declined. Searching for new matches...', type: 'error' });
      setTimeout(() => {
        if (socketRef.current && isSearching) {
          socketRef.current.emit('start-search');
        }
      }, 2000);
    });

    socketRef.current.on('interview-ended', () => {
      setUser(prev => ({ ...prev, isInterviewing: false }));
    });

    // Messaging events
    socketRef.current.on('new-message', (messageData) => {
      console.log('New message received:', messageData);
      // Update conversations list
      fetchConversations();
      // If this chat is open, add the message (use ref to avoid stale closure)
      if (activeChatRef.current && activeChatRef.current._id === messageData.senderId) {
        setChatMessages(prev => [...prev, {
          _id: messageData._id,
          content: messageData.content,
          timestamp: messageData.timestamp,
          isOwn: false,
          read: false
        }]);
        // Mark as read since chat is open
        socketRef.current?.emit('mark-read', { odejId: messageData.senderId });
      }
    });

    socketRef.current.on('message-sent', (messageData) => {
      console.log('Message sent confirmed:', messageData);
    });

    socketRef.current.on('conversation-created', ({ odejId }) => {
      console.log('Conversation created with:', odejId);
      fetchConversations();
    });

    socketRef.current.on('messages-read', ({ odejId }) => {
      // Update UI to show messages were read (use ref to avoid stale closure)
      if (activeChatRef.current && activeChatRef.current._id === odejId) {
        setChatMessages(prev => prev.map(msg => ({ ...msg, read: true })));
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Timer countdown when match is found
  useEffect(() => {
    if (matchedUser && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [matchedUser]);

  // Scroll to bottom of chat
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Fetch matches when searching
  useEffect(() => {
    if (isSearching && user.location?.latitude && user.location?.longitude) {
      fetchMatches();
    }
  }, [isSearching, searchRadius, selectedTags, user.location]);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setConversations(data.data.conversations);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchChatMessages = async (odejId) => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${odejId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setChatMessages(data.data.conversation.messages);
        // Mark as read via socket
        socketRef.current?.emit('mark-read', { odejId });
        // Refresh conversations to update unread count
        fetchConversations();
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const openChat = (conversation) => {
    setActiveChat(conversation.otherUser);
    fetchChatMessages(conversation.otherUser._id);
  };

  const closeChat = () => {
    setActiveChat(null);
    setChatMessages([]);
    setMessageInput('');
  };

  const sendMessage = () => {
    if (!messageInput.trim() || !activeChat) return;

    const content = messageInput.trim();
    setMessageInput('');

    // Optimistically add message to UI
    const tempMessage = {
      _id: Date.now(),
      content,
      timestamp: new Date(),
      isOwn: true,
      read: false
    };
    setChatMessages(prev => [...prev, tempMessage]);

    // Send via socket
    socketRef.current?.emit('send-message', {
      recipientId: activeChat._id,
      content
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/matches`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setMatchCount(data.data.matchCount);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      setLocationStatus('Getting your location...');
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const handleTagToggle = async (tagValue) => {
    let newTags = selectedTags.includes(tagValue)
      ? selectedTags.filter(t => t !== tagValue)
      : [...selectedTags, tagValue];
    setSelectedTags(newTags);

    if (isSearching) {
      try {
        const response = await fetch(`${API_URL}/auth/update`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ selectedTags: newTags })
        });
        const data = await response.json();
        if (data.success) {
          setUser(data.data.user);
          fetchMatches();
        }
      } catch (error) {
        console.error('Error updating tags:', error);
      }
    }
  };

  const handleInterviewToggle = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    const newLookingForInterview = !user.lookingForInterview;

    try {
      let updateData = { lookingForInterview: newLookingForInterview };

      if (newLookingForInterview) {
        if (isJobSeeker && selectedTags.length === 0) {
          setMessage({ text: 'Please select at least one job category', type: 'error' });
          setLoading(false);
          return;
        }

        try {
          const location = await getLocation();
          updateData.location = location;
          updateData.searchRadius = searchRadius;
          if (isJobSeeker) updateData.selectedTags = selectedTags;
          setLocationStatus(`Location found: ${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`);
        } catch (locError) {
          setMessage({ text: 'Unable to get location. Please enable location services.', type: 'error' });
          setLoading(false);
          setLocationStatus('Location access denied');
          return;
        }
      } else {
        setMatchCount(null);
        setLocationStatus('');
        setMatchedUser(null);
        setMatchId(null);
      }

      const response = await fetch(`${API_URL}/auth/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        if (newLookingForInterview) {
          setMessage({ text: 'Searching for interview matches...', type: 'success' });
          socketRef.current?.emit('start-search');
          setTimeout(fetchMatches, 500);
        } else {
          setMessage({ text: 'Search cancelled', type: 'success' });
        }
      } else {
        setMessage({ text: data.message, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Unable to update status', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRadiusChange = async (newRadius) => {
    setSearchRadius(newRadius);
    if (isSearching) {
      try {
        const response = await fetch(`${API_URL}/auth/update`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ searchRadius: newRadius })
        });
        const data = await response.json();
        if (data.success) {
          setUser(data.data.user);
          fetchMatches();
        }
      } catch (error) {
        console.error('Error updating radius:', error);
      }
    }
  };

  const handleAccept = () => {
    if (!matchId) return;
    setHasAccepted(true);
    socketRef.current?.emit('accept-match', { matchId });
  };

  const handleReject = () => {
    if (!matchId) return;
    socketRef.current?.emit('reject-match', { matchId });
    setMatchedUser(null);
    setMatchId(null);
  };

  const getRoleDisplayName = (roleValue) => roleValue === 'job_seeker' ? 'Job Seeker' : 'Hiring Manager';
  const getTagLabel = (value) => jobTags.find(t => t.value === value)?.label || value;
  const getTagIcon = (value) => jobTags.find(t => t.value === value)?.icon || '';

  const formatSalary = (min, max, currency) => {
    const symbol = currencies[currency] || '$';
    if (min && max) return `${symbol}${min.toLocaleString()} - ${symbol}${max.toLocaleString()}`;
    if (min) return `From ${symbol}${min.toLocaleString()}`;
    if (max) return `Up to ${symbol}${max.toLocaleString()}`;
    return null;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString();
  };

  // =====================
  // MATCH FOUND VIEW
  // =====================
  if (matchedUser) {
    const isViewingJobListing = matchedUser.role === 'hiring_manager';

    return (
      <>
        <style>{styles.global}</style>
        <div style={styles.pageContainer}>
          <div style={{ 
            ...styles.mainContent, 
            paddingRight: '20px',
            padding: '20px',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}>
            <div style={styles.bgCircle1}></div>
            <div style={styles.bgCircle2}></div>
            <div style={styles.bgCircle3}></div>

            <div style={{ ...styles.card, ...styles.cardMatchView }}>
              <div style={styles.logo}>
                <h1 style={styles.logoText}>🎉 Match Found!</h1>
                <p style={styles.subtitle}>Review their {isViewingJobListing ? 'job listing' : 'CV'} and decide</p>
              </div>

              <div style={styles.timer}>
                <div style={{ ...styles.timerText, ...(timeLeft <= 15 ? styles.timerWarning : {}) }}>{timeLeft}s</div>
                <div style={styles.timerLabel}>Time remaining to decide</div>
              </div>

              <div style={styles.matchCard}>
                <div style={styles.matchHeader}>
                  <div style={styles.matchAvatar}>{matchedUser.firstName?.charAt(0).toUpperCase()}</div>
                  <div>
                    <div style={styles.matchName}>{matchedUser.firstName} {matchedUser.lastName}</div>
                    <div style={styles.matchRole}>{isViewingJobListing ? 'Hiring Manager' : 'Job Seeker'}</div>
                  </div>
                </div>

                {isViewingJobListing ? (
                  matchedUser.jobListingFile && matchedFileUrl ? (
                    /* Show only the uploaded PDF */
                    <div style={styles.matchSection}>
                      <div style={{
                        background: 'white',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid rgba(124, 152, 133, 0.2)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      }}>
                        <iframe
                          src={`${matchedFileUrl}#view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                          style={{
                            width: '100%',
                            height: '700px',
                            border: 'none',
                            display: 'block',
                          }}
                          title="Job Listing Document"
                        />
                      </div>
                    </div>
                  ) : matchedUser.jobListing ? (
                    /* Show manually entered job listing */
                    <>
                      <div style={styles.matchSection}>
                        <div style={styles.matchSectionTitle}>Company</div>
                        <div style={styles.matchText}>{matchedUser.jobListing.companyName || 'Not specified'}</div>
                      </div>
                      <div style={styles.matchSection}>
                        <div style={styles.matchSectionTitle}>Position</div>
                        <div style={styles.matchText}>{matchedUser.jobListing.roleName || 'Not specified'}</div>
                      </div>
                      <div style={styles.matchSection}>
                        {matchedUser.jobListing.jobTag && (
                          <span style={{ ...styles.badge, ...styles.badgeCategory }}>{getTagIcon(matchedUser.jobListing.jobTag)} {getTagLabel(matchedUser.jobListing.jobTag)}</span>
                        )}
                        <span style={{ ...styles.badge, ...styles.badgeEmployment }}>{matchedUser.jobListing.employmentType === 'full_time' ? '🕐 Full Time' : '⌛ Part Time'}</span>
                      </div>
                      {matchedUser.jobListing.description && (
                        <div style={styles.matchSection}>
                          <div style={styles.matchSectionTitle}>Job Description</div>
                          <div style={styles.matchText}>{matchedUser.jobListing.description}</div>
                        </div>
                      )}
                      {formatSalary(matchedUser.jobListing.salaryMin, matchedUser.jobListing.salaryMax, matchedUser.jobListing.currency) && (
                        <div style={styles.matchSection}>
                          <div style={styles.matchSectionTitle}>Salary</div>
                          <div style={styles.salaryText}>{formatSalary(matchedUser.jobListing.salaryMin, matchedUser.jobListing.salaryMax, matchedUser.jobListing.currency)}<span style={{ fontSize: '14px', color: '#6b8080', fontWeight: '400' }}> / year</span></div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={styles.matchText}>No job listing information available</div>
                  )
                ) : (
                  matchedUser.cvFile && matchedFileUrl ? (
                    /* Show only the uploaded CV PDF */
                    <div style={styles.matchSection}>
                      <div style={{
                        background: 'white',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid rgba(124, 152, 133, 0.2)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      }}>
                        <iframe
                          src={`${matchedFileUrl}#view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                          style={{
                            width: '100%',
                            height: '700px',
                            border: 'none',
                            display: 'block',
                          }}
                          title="CV Document"
                        />
                      </div>
                    </div>
                  ) : matchedUser.cv ? (
                    /* Show manually entered CV */
                    <>
                      {(matchedUser.cv.age || matchedUser.cv.nationality) && (
                        <div style={styles.matchSection}>
                          <div style={styles.matchSectionTitle}>About</div>
                          <div style={styles.matchText}>{[matchedUser.cv.age && `${matchedUser.cv.age} years old`, matchedUser.cv.nationality].filter(Boolean).join(' • ')}</div>
                        </div>
                      )}
                      {matchedUser.cv.summary && (
                        <div style={styles.matchSection}>
                          <div style={styles.matchSectionTitle}>Professional Summary</div>
                          <div style={styles.matchText}>{matchedUser.cv.summary}</div>
                        </div>
                      )}
                      {matchedUser.cv.roles?.length > 0 && (
                        <div style={styles.matchSection}>
                          <div style={styles.matchSectionTitle}>Work Experience</div>
                          {matchedUser.cv.roles.map((role, i) => (
                            <div key={i} style={styles.matchItem}>
                              <div style={styles.matchItemTitle}>{role.title}</div>
                              <div style={styles.matchItemSub}>{role.company} {role.years && `• ${role.years}`}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {matchedUser.cv.education?.length > 0 && (
                        <div style={styles.matchSection}>
                          <div style={styles.matchSectionTitle}>Education</div>
                          {matchedUser.cv.education.map((edu, i) => (
                            <div key={i} style={styles.matchItem}>
                              <div style={styles.matchItemTitle}>{edu.degree}</div>
                              <div style={styles.matchItemSub}>{edu.institution} {edu.year && `• ${edu.year}`}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={styles.matchText}>No CV information available</div>
                  )
                )}
              </div>

              {!hasAccepted ? (
                <div style={styles.actionButtons}>
                  <button onClick={handleReject} style={styles.rejectButton}>✕ Decline</button>
                  <button onClick={handleAccept} style={styles.acceptButton}>✓ Accept Interview</button>
                </div>
              ) : (
                <div style={styles.waitingText}>✓ You accepted! Waiting for {matchedUser.firstName} to respond...</div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // =====================
  // NORMAL HOME VIEW
  // =====================
  const isHiringManager = user.role === 'hiring_manager';
  
  return (
    <>
      <style>{styles.global}</style>
      
      {/* Navigation for hiring managers */}
      {isHiringManager && (
        <nav style={styles.nav}>
          <div style={styles.navInner}>
            <span style={styles.navLogo}>Instaview</span>
            
            <div style={styles.navLinks}>
              <button 
                onClick={() => {}} 
                style={{ ...styles.navLink, ...styles.navLinkActive }}
              >
                🏠 Home
              </button>
              <button 
                onClick={() => navigate('/candidates')} 
                style={styles.navLink}
              >
                👥 Candidates
              </button>
            </div>
          </div>
        </nav>
      )}
      
      <div style={{ 
        ...styles.pageContainer,
        paddingTop: isHiringManager ? '70px' : '0'
      }}>
        <div style={styles.mainContent}>
          <div style={styles.bgCircle1}></div>
          <div style={styles.bgCircle2}></div>
          <div style={styles.bgCircle3}></div>
          
          <div style={styles.card}>
            <div style={styles.logo}>
              <h1 style={styles.logoText}>Instaview</h1>
              <p style={styles.subtitle}>Your dashboard</p>
            </div>

            {message.text && (
              <div style={{ ...styles.message, ...(message.type === 'error' ? styles.messageError : styles.messageSuccess) }}>
                {message.text}
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <div style={styles.avatar}>{user.firstName.charAt(0).toUpperCase()}</div>
              <h2 style={styles.welcomeText}>Welcome back, {user.firstName}</h2>
              <p style={styles.userEmail}>{user.email}</p>
              <div style={{ textAlign: 'center' }}>
                <span style={{ ...styles.userRole, ...(user.role === 'job_seeker' ? styles.roleJobSeeker : styles.roleHiringManager) }}>
                  {getRoleDisplayName(user.role)}
                </span>
              </div>
              
              <div style={styles.divider}></div>

              <div style={styles.interviewButtonContainer}>
                <button
                  onClick={handleInterviewToggle}
                  disabled={loading}
                  style={{ ...styles.getInterviewButton, ...(isSearching ? styles.getInterviewButtonSearching : styles.getInterviewButtonActive) }}
                >
                  {loading ? 'Updating...' : isSearching ? (<><span style={styles.searchingText}>🔍</span><span>Cancel Search</span></>) : (<><span>🎯</span><span>Get Interview!</span></>)}
                </button>

                {isSearching && (
                  <>
                    <p style={styles.statusMessage}>Looking for {isJobSeeker ? 'hiring managers' : 'candidates'}...</p>
                    {matchCount !== null && <p style={styles.matchCount}>{matchCount} potential match{matchCount !== 1 ? 'es' : ''} found</p>}
                  </>
                )}

                {locationStatus && <p style={styles.locationStatus}>{locationStatus}</p>}
              </div>

              <div style={styles.sliderContainer}>
                <div style={styles.sliderLabel}>
                  <span style={styles.sliderTitle}>Search Distance</span>
                  <span style={styles.sliderValue}>{searchRadius} km</span>
                </div>
                <input type="range" min="100" max="500" step="100" value={searchRadius} onChange={(e) => handleRadiusChange(parseInt(e.target.value))} style={styles.slider} />
                <div style={styles.sliderMarks}><span>100</span><span>200</span><span>300</span><span>400</span><span>500</span></div>
              </div>

              {isJobSeeker && (
                <div style={{ ...styles.sliderContainer, marginTop: '12px' }}>
                  <div style={styles.tagSectionTitle}>Job Categories ({selectedTags.length} selected)</div>
                  <div style={styles.tagGrid}>
                    {jobTags.map((tag) => (
                      <button key={tag.value} type="button" onClick={() => handleTagToggle(tag.value)} style={{ ...styles.tagButton, ...(selectedTags.includes(tag.value) ? styles.tagButtonSelected : {}) }}>
                        <span style={styles.tagIcon}>{tag.icon}</span>
                        <span>{tag.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={styles.divider}></div>
              
              <div style={styles.buttonGroup}>
                <button onClick={() => navigate(isJobSeeker ? '/createcv' : '/createjoblisting')} style={{ ...styles.button, ...styles.buttonPrimary }}>
                  Edit {isJobSeeker ? 'CV' : 'Job Listing'}
                </button>
                <button onClick={handleLogout} style={{ ...styles.button, ...styles.buttonSecondary }}>Sign Out</button>
              </div>
            </div>
          </div>
        </div>

        {/* Messaging Sidebar */}
        <div style={styles.messagingSidebar}>
          <div style={styles.sidebarHeader}>
            <span style={styles.sidebarTitle}>💬 Messages</span>
          </div>
          
          <div style={styles.conversationsList}>
            {conversations.length === 0 ? (
              <div style={styles.emptyConversations}>
                <div style={styles.emptyIcon}>💬</div>
                <div style={styles.emptyText}>No conversations yet.<br/>Complete an interview to start chatting!</div>
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv._id}
                  style={{
                    ...styles.conversationItem,
                    ...(activeChat?._id === conv.otherUser._id ? styles.conversationItemActive : {})
                  }}
                  onClick={() => openChat(conv)}
                >
                  <div style={styles.conversationAvatar}>
                    {conv.otherUser.firstName?.charAt(0).toUpperCase()}
                  </div>
                  <div style={styles.conversationInfo}>
                    <div style={styles.conversationName}>
                      {conv.otherUser.firstName} {conv.otherUser.lastName}
                    </div>
                    <div style={styles.conversationPreview}>
                      {conv.lastMessage ? (conv.lastMessage.isOwn ? 'You: ' : '') + conv.lastMessage.content : 'No messages yet'}
                    </div>
                  </div>
                  <div style={styles.conversationMeta}>
                    {conv.lastMessage && (
                      <span style={styles.conversationTime}>{formatTime(conv.lastMessage.timestamp)}</span>
                    )}
                    {conv.unreadCount > 0 && (
                      <span style={styles.unreadBadge}>{conv.unreadCount}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Panel */}
        {activeChat && (
          <div style={styles.chatPanel}>
            <div style={styles.chatHeader}>
              <div style={styles.chatHeaderAvatar}>
                {activeChat.firstName?.charAt(0).toUpperCase()}
              </div>
              <div style={styles.chatHeaderInfo}>
                <div style={styles.chatHeaderName}>{activeChat.firstName} {activeChat.lastName}</div>
                <div style={styles.chatHeaderRole}>{getRoleDisplayName(activeChat.role)}</div>
              </div>
              <button style={styles.chatCloseBtn} onClick={closeChat}>×</button>
            </div>
            
            <div style={styles.chatMessages} ref={chatMessagesRef}>
              {chatMessages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6b8080', padding: '20px' }}>
                  No messages yet. Say hello! 👋
                </div>
              ) : (
                chatMessages.map((msg) => (
                  <div
                    key={msg._id}
                    style={{
                      ...styles.chatMessage,
                      ...(msg.isOwn ? styles.chatMessageOwn : styles.chatMessageOther)
                    }}
                  >
                    {msg.content}
                  </div>
                ))
              )}
            </div>
            
            <div style={styles.chatInputContainer}>
              <input
                type="text"
                placeholder="Type a message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyPress={handleKeyPress}
                style={styles.chatInput}
              />
              <button onClick={sendMessage} style={styles.chatSendBtn}>➤</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default HomePage;